<script setup>
import { ref, watch } from 'vue';
import { VueTelInput } from 'vue-tel-input';
import 'vue-tel-input/vue-tel-input.css';

/**
 * International phone input — wraps `vue-tel-input` (the same library used by the
 * /register and Edit Profile forms) so it accepts numbers from any country.
 *
 * The exposed `v-model` holds the canonical **E.164** number (e.g. `+16025550100`)
 * when the input is a valid number, and '' otherwise (empty OR non-empty-but-invalid).
 * It never holds a raw/partial string — callers can rely on "E.164 or empty". E.164 is
 * the rawest form we can store that still maps back to a formatted display for any
 * country. Validity is surfaced via `@validate` as `{ isValid, isEmpty, e164 }` so the
 * consumer decides required vs. optional (block submission when `!isEmpty && !isValid`)
 * — this component never renders its own error text (that's the caller's FormField).
 */
const model = defineModel({ type: String, default: '' });
const emit = defineEmits(['validate', 'blur']);

defineProps({
  id: { type: String, default: undefined },
  error: { type: Boolean, default: false },
  ariaDescribedby: { type: String, default: undefined },
  placeholder: { type: String, default: 'Phone Number' }
});

// `vue-tel-input` binds to the display string the user is typing; `model` mirrors it as
// E.164. They're kept separate: the display keeps whatever the user typed (even while
// invalid) so we never wipe in-progress input, while `model` stays canonical. Seeded
// once from an initial model value; the user drives it thereafter.
const displayNumber = ref(model.value || '');

// vue-tel-input @validate payload (v9): { number (E.164), valid, countryCode, ... }.
const onValidate = (payload = {}) => {
  const isValid = Boolean(payload.isValid ?? payload.valid);
  const isEmpty = !(displayNumber.value || '').trim();
  // Canonical contract: E.164 when the number is valid, '' otherwise. A non-empty
  // invalid number stays out of the model (consumers block submit via the emitted
  // { isEmpty: false, isValid: false }); the raw text remains only in the display.
  model.value = isValid && payload.number ? payload.number : '';
  emit('validate', { isValid, isEmpty, e164: payload.number || '' });
};

// vue-tel-input doesn't reliably re-emit `validate` when the input is cleared, so a
// previously-invalid number stayed stuck in the error state after being deleted. The
// field is optional, so emptying it must clear that error — re-assert the empty
// contract ({ isValid: false, isEmpty: true }), which consumers treat as acceptable
// via `!isEmpty && !isValid`. Runs whenever the display goes blank, independent of the
// library's validate timing. (Non-empty changes are still driven by `onValidate` above.)
watch(displayNumber, (val) => {
  if (!(val || '').trim()) {
    model.value = '';
    emit('validate', { isValid: false, isEmpty: true, e164: '' });
  }
});

// Exposed for unit tests (the harness stubs vue-tel-input, so we drive this directly).
defineExpose({ onValidate, displayNumber });
</script>

<template>
  <!--
    Wrapper is required: `vue-tel-input`'s root element is `.vue-tel-input`, so a
    scoped `:deep(.vue-tel-input)` compiles to a *descendant* selector and never
    matches the root. Styling (and the error class) must hang off this wrapper.
  -->
  <div class="phone-input" :class="{ 'phone-input--error': error }">
    <vue-tel-input
      v-model="displayNumber"
      mode="international"
      :autoDefaultCountry="false"
      :defaultCountry="'us'"
      :preferredCountries="['us', 'gb']"
      :validCharactersOnly="true"
      :inputOptions="{ id, placeholder, 'aria-describedby': ariaDescribedby }"
      @validate="onValidate"
      @blur="emit('blur')"
    />
  </div>
</template>

<style scoped>
/* Match the outlined look + height of the modal's other fields
   (v-text-field density="comfortable" → 48px). */
.phone-input :deep(.vue-tel-input) {
  height: 48px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  box-shadow: none;
}

/* Kill vue-tel-input's default blue focus glow/border; use the app's dark focus. */
.phone-input :deep(.vue-tel-input:focus-within) {
  border-color: #191919;
  box-shadow: none;
}

/* Error border (parity with the consent screen design token, error/default).
   Listed after focus and covering the focused state so it stays red even when
   the invalid field is refocused. */
.phone-input--error :deep(.vue-tel-input),
.phone-input--error :deep(.vue-tel-input:focus-within) {
  border-color: #cc2f2f;
}

.phone-input :deep(.vti__input) {
  font-size: 16px;
  border-radius: 0 4px 4px 0;
}
</style>
